/**
 * Vorhaben eines Bereichs (F-01, F-11): Ziele und Projekte mit Fortschritt,
 * dazu die Deadlines. Alles anklickbar (Leitlinie 5).
 */
import { Link } from 'react-router-dom';
import { Flag, FolderKanban, Target } from 'lucide-react';

import { PriorityDot } from '@/ui/composites/PriorityDot';
import { ProgressBar } from '@/modules/goals/components/ProgressBar';
import { DeadlineBadge } from '@/modules/deadlines/DeadlineBadge';
import { useDeadlines } from '@/modules/deadlines/useDeadlines';
import { deadlinePfad } from '@/modules/deadlines/useDeadlineOpen';
import { goalOverallProgress } from '@/domain/goals/overall';
import { projectProgress } from '@/domain/projects/progress';
import { goalPriority, goalPriorityKey, projectPriority, projectPriorityRank } from '@/domain/priority';
import { DEADLINE_KIND_LABEL } from '@/domain/deadlines';
import { pfad } from '@/app/fokus';
import type { Area, Goal, Project, Task } from '@/domain/types';

function Abschnitt({ titel, icon: Icon, children }: { titel: string; icon: typeof Target; children: React.ReactNode }) {
  return (
    <section aria-label={titel} className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3.5">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold"><Icon className="size-4 text-ink-faint" />{titel}</h2>
      {children}
    </section>
  );
}

export function BereichZiele({ area, goals, projects, tasks }: { area: Area; goals: Goal[]; projects: Project[]; tasks: Task[] }) {
  const laufend = goals.filter((g) => g.status === 'offen' || g.status === 'laeuft')
    .sort((a, b) => projectPriorityRank(goalPriority(a)) - projectPriorityRank(goalPriority(b)) || a.title.localeCompare(b.title, 'de'));
  const fertig = goals.length - laufend.length;
  const aufgabenVon = (g: Goal) => {
    const projektIds = new Set(projects.filter((p) => p.goalId === g.id).map((p) => p.id));
    return tasks.filter((t) => t.goalId === g.id || (t.projectId && projektIds.has(t.projectId)));
  };

  return (
    <Abschnitt titel="Ziele" icon={Target}>
      {laufend.length === 0 && <p className="text-sm text-ink-faint">Keine laufenden Ziele in {area.name}.</p>}
      <ul className="flex flex-col gap-2">
        {laufend.map((g) => {
          const f = goalOverallProgress(g, aufgabenVon(g));
          return (
            <li key={g.id} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-sm">
                <PriorityDot priority={goalPriorityKey(goalPriority(g))} />
                <Link to={pfad.ziel(g.id)} className="min-w-0 flex-1 truncate hover:underline">{g.title}</Link>
                <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">{Math.round(f.ratio * 100)} %</span>
              </div>
              <ProgressBar value={f.ratio} color={area.color} label={`Fortschritt ${g.title}`} />
            </li>
          );
        })}
      </ul>
      {fertig > 0 && <p className="text-[11px] text-ink-faint">{fertig} erreicht oder verworfen</p>}
    </Abschnitt>
  );
}

export function BereichProjekte({ area, projects, tasks }: { area: Area; projects: Project[]; tasks: Task[] }) {
  const offen = projects.filter((p) => p.status === 'aktiv' || p.status === 'pausiert')
    .sort((a, b) => Number(a.status === 'pausiert') - Number(b.status === 'pausiert')
      || projectPriorityRank(projectPriority(a)) - projectPriorityRank(projectPriority(b))
      || a.title.localeCompare(b.title, 'de'));
  const rest = projects.length - offen.length;

  return (
    <Abschnitt titel="Projekte" icon={FolderKanban}>
      {offen.length === 0 && <p className="text-sm text-ink-faint">Keine aktiven Projekte in {area.name}.</p>}
      <ul className="flex flex-col gap-2">
        {offen.map((p) => {
          const f = projectProgress(tasks.filter((t) => t.projectId === p.id));
          return (
            <li key={p.id} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-sm">
                <PriorityDot priority={projectPriority(p)} />
                <Link to={pfad.projekt(p.id)} className="min-w-0 flex-1 truncate hover:underline">{p.title}</Link>
                {p.status === 'pausiert' && <span className="shrink-0 text-[11px] text-ink-faint">pausiert</span>}
                <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">{f.done}/{f.total}</span>
              </div>
              <ProgressBar value={f.ratio} color={area.color} label={`Fortschritt ${p.title}`} />
            </li>
          );
        })}
      </ul>
      {rest > 0 && <p className="text-[11px] text-ink-faint">{rest} abgeschlossen oder für irgendwann</p>}
    </Abschnitt>
  );
}

export function BereichDeadlines({ area }: { area: Area }) {
  const liste = useDeadlines(area.id);
  return (
    <Abschnitt titel="Deadlines" icon={Flag}>
      {liste.length === 0 && <p className="text-sm text-ink-faint">Keine offenen Deadlines.</p>}
      <ul className="flex flex-col gap-1">
        {liste.slice(0, 10).map((d) => (
          <li key={`${d.kind}:${d.id}`} className="flex items-center gap-2 text-sm">
            <Link to={deadlinePfad(d)} title={DEADLINE_KIND_LABEL[d.kind]} className="min-w-0 flex-1 truncate hover:underline">{d.title}</Link>
            <DeadlineBadge date={d.date} time={d.time} />
          </li>
        ))}
      </ul>
    </Abschnitt>
  );
}
